import { z } from "zod";

const VibeAuditDeploymentApprovalDecisionShape = {
  vibeApprovalRequestId: z.string().uuid(),
  vibeDeploymentId: z.string().uuid(),
  deciderUserId: z.string().min(1),
  /// True iff this decision was the one that crossed the request's
  /// `requiredApprovals` threshold (APPROVED) or any-reject (REJECTED).
  /// False for in-flight approves that did not yet meet the threshold
  /// — those don't transition status and therefore don't emit at all,
  /// but the field exists so a future "show every decision" event
  /// type stays additive.
  decisive: z.boolean(),
  note: z.string().nullable()
};

/**
 * Emitted when a reviewer decision moves the approval request to
 * APPROVED. The deployment status flips from AWAITING_APPROVAL on
 * the next deployer poll; the audit row is recorded at the moment
 * the decision adapter commits.
 */
export const VibeAuditDeploymentApprovedPayloadSchema = z.object({
  eventType: z.literal("DEPLOYMENT_APPROVED"),
  ...VibeAuditDeploymentApprovalDecisionShape
});
export type VibeAuditDeploymentApprovedPayload = z.infer<
  typeof VibeAuditDeploymentApprovedPayloadSchema
>;

/**
 * Emitted when a reviewer decision moves the approval request to
 * REJECTED. The deployment terminates in FAILED on the next
 * deployer poll; the audit row records the reject + the note (the
 * customer-visible reason).
 */
export const VibeAuditDeploymentRejectedPayloadSchema = z.object({
  eventType: z.literal("DEPLOYMENT_REJECTED"),
  ...VibeAuditDeploymentApprovalDecisionShape
});
export type VibeAuditDeploymentRejectedPayload = z.infer<
  typeof VibeAuditDeploymentRejectedPayloadSchema
>;

/**
 * Emitted by the scheduled sweeper when a PENDING approval crosses
 * `expiresAt` without a decision. The sweeper writes one audit row
 * PER expired request (not a single bulk event) so the feed remains
 * informative and the per-app timeline picks up the right entries.
 * `actorUserId` is null for these — the sweeper is a system actor.
 */
export const VibeAuditApprovalExpiredPayloadSchema = z.object({
  eventType: z.literal("APPROVAL_EXPIRED"),
  vibeApprovalRequestId: z.string().uuid(),
  vibeDeploymentId: z.string().uuid()
});
export type VibeAuditApprovalExpiredPayload = z.infer<typeof VibeAuditApprovalExpiredPayloadSchema>;
